import { defineStore } from "pinia";
import { getMasterSToken, getMasterSUid, resolveMasterSUid } from "../api/master-s";
import type { MasterSChatRecord } from "@variants/seer/master-s/shared/types/chat-record";
import type { MasterSRoomInfo } from "@variants/seer/master-s/shared/types/room-info";
import {
	normalizeMasterSChatRecords,
	recordToChatBubble,
} from "@variants/seer/master-s/shared/utils/chat-record";
import { getMasterSWsLang, t } from "@variants/seer/master-s/shared/utils/i18n";
import { isSandbox } from "~/utils/common";

const MASTER_S_WS_PROD = "wss://ws-digitalhuman.seeronnet.com/digital_human";
const MASTER_S_WS_SANDBOX = "wss://sandbox-ws-digitalhuman.seeronnet.com/digital_human";

export const useMasterSSocketStore = defineStore("masterSSocket", () => {
	const config = useRuntimeConfig();

	const roomInfo = ref<MasterSRoomInfo | null>(null);
	const connected = ref(false);
	const messages = ref<any[]>([]);
	/** 当前会话气泡列表，跨页面跳转（如切换档案）保留 */
	const chatList = ref<
		Array<{
			role: "user" | "ai";
			text?: string;
			image?: string;
			markdown?: boolean;
			/** 正式答复锚点，用于滚到答复开头 */
			anchorId?: string;
		}>
	>([]);
	/** 历史分页游标（与 records 一致：reverse 追加后末条 id） */
	const historyLastMsgId = ref(0);
	const historyAll = ref(false);
	const historyLoading = ref(false);

	let ws: WebSocket | null = null;
	let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
	let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	let isClosed = false;
	let connecting = false;
	const pendingPayloads: Array<object | string> = [];
	const messageListeners = new Set<(msg: any) => void>();

	const hasMasterSAuth = () => Boolean(getMasterSToken());

	function resolveWsHost() {
		if (isSandbox) return MASTER_S_WS_SANDBOX;
		return (
			config.public.masterSWebSocketHost || config.public.digitalHumanSocketHost || MASTER_S_WS_PROD
		);
	}

	function buildWsUrl() {
		const currentUid = getMasterSUid();
		if (!currentUid) return "";
		const params = new URLSearchParams({
			lang: getMasterSWsLang(),
			platform: "71",
			uid: currentUid,
			channel: "h5",
			version: "4.2.2",
		});
		return `${resolveWsHost()}?${params.toString()}`;
	}

	async function connect() {
		if (!import.meta.client || connecting) return;
		if (!hasMasterSAuth()) return;
		if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

		connecting = true;
		isClosed = false;
		try {
			const uid = await resolveMasterSUid(true);
			if (!uid || isClosed) return;
			if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
				return;

			const url = buildWsUrl();
			if (!url || isClosed) return;

			ws = new WebSocket(url);

			ws.onopen = () => {
				connected.value = true;
				requestRoomInfo();
				flushPendingPayloads();
				startHeartbeat();
			};

			ws.onmessage = event => {
				if (event.data === "pong") return;
				try {
					const modelJson = JSON.parse(event.data);
					handleMessage(modelJson);
				} catch (error) {
					console.error("MasterS WS parse error", error);
				}
			};

			ws.onclose = () => {
				connected.value = false;
				stopHeartbeat();
				if (!isClosed) {
					clearReconnectTimer();
					reconnectTimer = setTimeout(() => {
						void connect();
					}, 3000);
				}
			};

			ws.onerror = () => {
				connected.value = false;
			};
		} finally {
			connecting = false;
		}
	}

	function clearReconnectTimer() {
		if (reconnectTimer) {
			clearTimeout(reconnectTimer);
			reconnectTimer = null;
		}
	}

	function startHeartbeat() {
		stopHeartbeat();
		heartbeatTimer = setInterval(() => {
			if (ws?.readyState === WebSocket.OPEN) {
				ws.send("ping");
				if (!roomInfo.value) requestRoomInfo();
			}
		}, 5000);
	}

	function stopHeartbeat() {
		if (heartbeatTimer) {
			clearInterval(heartbeatTimer);
			heartbeatTimer = null;
		}
	}

	function handleMessage(modelJson: any) {
		const type = modelJson.type ?? "";
		if (type === "roomInfo") {
			const dataJson = modelJson.data;
			const info = dataJson?.data ?? dataJson;
			const prevSelect = Number(roomInfo.value?.select_model || 0);
			if (info && info.select_model != null) {
				info.select_model = Number(info.select_model);
			}
			// set_model 后回包可能短暂未同步，保留本地已选，避免问题面板被卸掉
			if (info && !Number(info.select_model) && prevSelect) {
				info.select_model = prevSelect;
			}
			roomInfo.value = info;
			return;
		}
		messages.value.push(modelJson);
		messageListeners.forEach(fn => fn(modelJson));
	}

	function onMessage(fn: (msg: any) => void) {
		messageListeners.add(fn);
		return () => messageListeners.delete(fn);
	}

	function flushPendingPayloads() {
		while (pendingPayloads.length && ws?.readyState === WebSocket.OPEN) {
			const payload = pendingPayloads.shift();
			if (payload) sendRaw(payload);
		}
	}

	function sendRaw(payload: object | string) {
		if (ws?.readyState === WebSocket.OPEN) {
			ws.send(typeof payload === "string" ? payload : JSON.stringify(payload));
			return;
		}
		pendingPayloads.push(payload);
		void connect();
	}

	function requestRoomInfo() {
		sendRaw({ type: "roomInfo", data: {} });
	}

	/** 与参考项目 masterSocket.getMessageList 一致 */
	function getMessageList(lastMsgId: number) {
		sendRaw({ type: "msgList", data: { last_msg_id: lastMsgId } });
	}

	/** 等待一页 msgList 回包（封装成 Promise，协议与参考项目相同） */
	function fetchMessageList(lastMsgId: number, timeoutMs = 8000): Promise<MasterSChatRecord[]> {
		return new Promise(resolve => {
			let settled = false;
			const finish = (list: MasterSChatRecord[]) => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				unsub();
				resolve(list);
			};
			const unsub = onMessage(msg => {
				if (msg?.type !== "msgList") return;
				const data = Array.isArray(msg.data) ? msg.data : [];
				finish(data as MasterSChatRecord[]);
			});
			const timer = setTimeout(() => finish([]), timeoutMs);
			getMessageList(lastMsgId);
		});
	}

	function resetHistoryPaging() {
		historyLastMsgId.value = 0;
		historyAll.value = false;
		historyLoading.value = false;
	}

	/**
	 * 进会话拉历史写入 chatList。
	 * 分页协议对齐参考项目 records.vue；多页合并后按时间升序展示，保证刷新后上旧下新。
	 */
	async function loadChatHistory(): Promise<boolean> {
		resetHistoryPaging();
		const list: MasterSChatRecord[] = [];
		let lastMsgId = 0;
		let isAll = false;

		while (!isAll && list.length < 20) {
			const dataJson = await fetchMessageList(lastMsgId);
			const items = dataJson.map(i => i as MasterSChatRecord).reverse();
			list.push(...items);

			if (items.length) {
				lastMsgId = items[items.length - 1].id;
			}
			// 参考项目：空页且已有游标视为拉完；首屏空列表也结束，避免死循环
			if (dataJson.length === 0) {
				isAll = true;
			}
		}

		historyLastMsgId.value = lastMsgId;
		historyAll.value = isAll;

		const bubbles = normalizeMasterSChatRecords(list)
			.map(recordToChatBubble)
			.filter((m): m is NonNullable<typeof m> => Boolean(m));
		chatList.value = bubbles;
		return bubbles.length > 0;
	}

	/**
	 * 上滑加载更早一页，prepend 到 chatList。
	 * 协议与 records 下一页相同；会话 UI 插入顶部以保持时间正序。
	 * @returns 新增气泡数
	 */
	async function loadOlderChatHistory(): Promise<number> {
		if (historyAll.value || historyLoading.value || !historyLastMsgId.value) return 0;

		historyLoading.value = true;
		try {
			const dataJson = await fetchMessageList(historyLastMsgId.value);
			if (dataJson.length === 0) {
				historyAll.value = true;
				return 0;
			}

			const items = dataJson.map(i => i as MasterSChatRecord).reverse();
			historyLastMsgId.value = items[items.length - 1].id;

			const bubbles = normalizeMasterSChatRecords(items)
				.map(recordToChatBubble)
				.filter((m): m is NonNullable<typeof m> => Boolean(m));
			if (bubbles.length) {
				chatList.value = [...bubbles, ...chatList.value];
			}
			return bubbles.length;
		} finally {
			historyLoading.value = false;
		}
	}

	function sendText(text: string) {
		sendRaw({ type: "sendText", data: { text } });
	}

	function sendBazi(birthday: string, sex: string, question: string) {
		const q = t("我的生日@birthday性别@sex问题@question", { birthday, sex, question });
		sendRaw({ type: "askBazi", data: { question: q } });
	}

	function sendTarotCard(image: string, layout: string, question: string) {
		sendRaw({ type: "sendTarotCard", data: { image, layout, question } });
	}

	function sendLiuYao(image: string, layout: string) {
		sendRaw({ type: "sendLiuYao", data: { image, layout } });
	}

	function clearChatList() {
		chatList.value = [];
		resetHistoryPaging();
	}

	function close() {
		isClosed = true;
		clearReconnectTimer();
		stopHeartbeat();
		ws?.close();
		ws = null;
		roomInfo.value = null;
		connected.value = false;
		chatList.value = [];
		resetHistoryPaging();
		pendingPayloads.length = 0;
	}

	return {
		roomInfo,
		connected,
		messages,
		chatList,
		historyLastMsgId,
		historyAll,
		historyLoading,
		connect,
		close,
		clearChatList,
		onMessage,
		requestRoomInfo,
		getMessageList,
		fetchMessageList,
		loadChatHistory,
		loadOlderChatHistory,
		sendText,
		sendBazi,
		sendTarotCard,
		sendLiuYao,
	};
});
